<template>
  <div class="progress-bar">
    <div class="progress-bar__mobile-progress" v-if="activeStep !== 6">
      <span class="mobile-progress-bar"
        :style="{ width:  20 * (activeStep - 1) + '%' }"
        :data-text="20 * (activeStep - 1) + '%'">
      </span>
    </div>
    <h3 class="progress-bar__title">
      <span v-if="activeStep < 6" v-html="$tc('progress.title', stepsLeft, { stepsLeft: stepsLeft })"></span>
      <span v-if="activeStep >= 6" v-html="$t('progress.titleCompleted')"></span>
    </h3>
    <ol class="progress-bar__progress d-print-none">
      <li class="progress__item" :class="getClasses(1)" @click="goToStep(1)">
        <iconDone class="progress__item__icon-done" v-if="isDone(1)"></iconDone>
        {{ $t('progress.step1') }}
      </li>
      <li class="progress__item" :class="getClasses(2)" @click="goToStep(2)">
        <iconDone class="progress__item__icon-done" v-if="isDone(2)"></iconDone>
        {{ $t('progress.step2') }}
      </li>
      <li class="progress__item" :class="getClasses(3)" @click="goToStep(3)">
        <iconDone class="progress__item__icon-done" v-if="isDone(3)"></iconDone>
        {{ $t('progress.step3') }}
      </li>
      <li class="progress__item" :class="getClasses(4)" @click="goToStep(4)">
        <iconDone class="progress__item__icon-done" v-if="isDone(4)"></iconDone>
        {{ $t('progress.step4') }}
      </li>
      <li class="progress__item" :class="getClasses(5)" @click="goToStep(5)">
        <iconDone class="progress__item__icon-done" v-if="isDone(5)"></iconDone>
        {{ $t('progress.step5') }}
      </li>
    </ol>
  </div>
</template>

<script>
import iconDone from '@/assets/icons/progress-done.svg'

export default {
  name: 'CiProgressBar',
  components: {
    iconDone,
  },
  props: {
    activeStep: {
      type: Number,
      required: true,
    },
  },
  computed: {
    stepsLeft() {
      return 5 - this.activeStep + 1
    },
  },
  methods: {
    goToStep(num) {
      if (this.activeStep > num) this.$emit('changeStep', num)
    },

    getClasses(step) {
      return {
        'progress__item--active': this.activeStep === step,
        'progress__item--done': this.activeStep > step,
        'progress__item--not-clickable': this.activeStep >= 6,
      }
    },

    isDone(step) {
      return this.activeStep > step
    },
  },
}
</script>
<style lang="scss">
.progress-bar__title {
  .steps-left {
    @include font('gotham_mediumregular');

    color: $primary-orange;
  }
}
</style>
<style lang="scss" scoped>
.progress-bar {
  margin: 40px 0 0 0;

  @include media-breakpoint-up(md) {
    margin: 55px 0 55px;
  }

  @include media-breakpoint-up(lg) {
    margin: 25px 0 55px;
  }

  @include media-breakpoint-up(xl) {
    margin: 60px 0 65px;
  }
}

.progress-bar__mobile-progress {
  background: #a2a2a2;
  border-radius: 2.5px;
  height: 8px;
  left: 0;
  margin: 20px 0;
  position: absolute;
  top: 13px;
  width: calc(100% - 120px);

  @include media-breakpoint-up(md) {
    display: none;
  }

  .mobile-progress-bar {
    background: $primary-orange;
    border-radius: 2.5px;
    color: $primary-orange;
    display: block;
    height: 100%;
    position: relative;
    transition: width 0.4s ease;
    width: 20%;

    &::after {
      color: inherit;
      content: attr(data-text);
      height: auto;
      position: absolute;
      right: 0;
      top: -25px;
    }
  }
}

.progress-bar__title {
  @include font('gotham_lightregular');
  @include font-format(1.125em, 1.375rem);

  text-align: center;
  margin: 0 auto 35px;
  max-width: 200px;

  @include media-breakpoint-up(md) {
    @include font-format(1.25rem, 1.188rem);

    margin: 0 0 65px;
    max-width: 100%;
  }

  @include media-breakpoint-up(xl) {
    @include font-format(1.375rem, 1.313rem);

    margin: 0 auto 80px;
  }

  @media print {
    max-width: 100%;
    margin: 0 0 30px;
  }
}

.progress-bar__progress {
  display: none;

  @include media-breakpoint-up(md) {
    display: flex;
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
}

.progress__item {
  flex: 1;
  position: relative;
  text-align: center;
  padding-top: 25px;
  transition: color 0.3s ease;

  &::before {
    background: #d8d8d8;
    content: '';
    display: block;
    height: 8px;
    left: 0;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 0;
    width: calc(100% - 17px);
    z-index: 5;
  }

  &::after {
    content: '';
    height: 26px;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 26px;
    background: white;
    border: 6px solid #d8d8d8;
    border-radius: 50%;
    transition: border 0.3s ease, width 0.3s ease, height 0.3s ease;
    z-index: 10;
  }

  &:first-child {
    &::before {
      display: none;
    }
  }
}

.progress__item__icon-done {
  left: 50%;
  position: absolute;
  top: 0;
  transform: translate(-50%, -50%);
  transition: transform 0.4s cubic-bezier(0.37, 0.01, 0, 0.98);
}

.progress__item--done {
  color: $secondary-orange;
  cursor: pointer;

  .no-touch &:hover .progress__item__icon-done {
    transform: translate(-50%, -50%) scale(1.2);
  }

  &::before {
    background: $primary-orange;
  }

  &::after {
    display: none;
  }
}

.progress__item--active {
  @include font('gotham_mediumregular');

  color: $primary-orange;

  &::before {
    background: linear-gradient(90deg, $primary-orange 0%, $secondary-orange 50%, $light-gray 100%);
  }

  &::after {
    height: 29px;
    width: 29px;
    border: 4px solid $primary-orange;
  }
}

.progress__item--not-clickable {
  pointer-events: none;
}
</style>
